<template>
  <div class="vehicle-details__breadcrumbs flex flex-wrap gap-2 items-center">
    <div class="vehicle-details__breadcrumbs-item">
      <NuxtLink
        class="text-small-normal hover:text-accent hover:underline transition-colors duration-150"
        :to="searchBaseUrl"
        >{{ t('components.carDetail.breadcrumbs.search') }}</NuxtLink
      >
    </div>
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
      <path
        d="M6.47135 11.7994L10.276 7.99477L6.47135 4.1901L5.52869 5.13277L8.39069 7.99477L5.52869 10.8568L6.47135 11.7994Z"
        fill="#37B5AA"
      />
    </svg>
    <div class="vehicle-details__breadcrumbs-item">
      <NuxtLink
        class="text-small-normal hover:text-accent hover:underline transition-colors duration-150"
        :to="`${searchBaseUrl}/${carDetail?.make?.slug}`"
        >{{ carDetail?.make?.name }}</NuxtLink
      >
    </div>
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
      <path
        d="M6.47135 11.7994L10.276 7.99477L6.47135 4.1901L5.52869 5.13277L8.39069 7.99477L5.52869 10.8568L6.47135 11.7994Z"
        fill="#37B5AA"
      />
    </svg>
    <NuxtLink
      class="text-small-normal hover:text-accent hover:underline transition-colors duration-150"
      :to="`${searchBaseUrl}/${carDetail?.make?.slug}/${activeBodyStyle?.slug}`"
      >{{ activeBodyStyle?.name }}</NuxtLink
    >
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
      <path
        d="M6.47135 11.7994L10.276 7.99477L6.47135 4.1901L5.52869 5.13277L8.39069 7.99477L5.52869 10.8568L6.47135 11.7994Z"
        fill="#37B5AA"
      />
    </svg>
    <span class="text-small-semibold cursor-default">{{ carDetail?.make?.name }} {{ carDetail?.model }}</span>
  </div>
</template>

<script lang="ts" setup>
  import type { GraphQLCarDetail } from '~/types/graphql';
  import type { BodyStyle } from '~/types/strapi';

  type BreadcrumbItem = { name: string; url: string };

  const { t } = useI18n();
  const { isLoading } = useLoadingIndicator();

  const injectedCarDetail = inject<GraphQLCarDetail>('carDetail');
  const router = useRouter();
  const runtimeConfig = useRuntimeConfig();

  const carDetail = ref(injectedCarDetail);
  const activeBodyStyle = ref<BodyStyle | undefined>();

  const computeActiveBodyStyle = () => {
    const currentBodyStyleParam = router.currentRoute.value.params.body_style;
    if (!currentBodyStyleParam) return undefined;

    if (!carDetail.value?.body_style?.length) return undefined;

    return (
      carDetail.value.body_style.find(body_style => body_style.slug === currentBodyStyleParam) || carDetail.value.body_style[0]
    );
  };

  activeBodyStyle.value = computeActiveBodyStyle();

  watch(isLoading, (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      carDetail.value = injectedCarDetail;
      activeBodyStyle.value = computeActiveBodyStyle();
    }
  });

  const searchBaseUrl = `/autos-kaufen`;

  const siteUrl = computed(() => (runtimeConfig.public.siteURL || '').replace(/\/$/, ''));
  const toAbsoluteUrl = (path: string) => (siteUrl.value ? `${siteUrl.value}${path}` : path);

  const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [
      {
        name: t('components.carDetail.breadcrumbs.search'),
        url: toAbsoluteUrl(searchBaseUrl),
      },
    ];

    const makeSlug = carDetail.value?.make?.slug;
    const makeName = carDetail.value?.make?.name;

    if (makeSlug && makeName) {
      items.push({
        name: makeName,
        url: toAbsoluteUrl(`${searchBaseUrl}/${makeSlug}`),
      });
    }

    if (makeSlug && activeBodyStyle.value?.slug && activeBodyStyle.value?.name) {
      items.push({
        name: activeBodyStyle.value.name,
        url: toAbsoluteUrl(`${searchBaseUrl}/${makeSlug}/${activeBodyStyle.value.slug}`),
      });
    }

    if (carDetail.value?.model) {
      const currentPath = router.currentRoute.value.fullPath;
      const label = [makeName, carDetail.value.model].filter(Boolean).join(' ');

      items.push({
        name: label,
        url: toAbsoluteUrl(currentPath),
      });
    }

    return items;
  });

  const breadcrumbJsonLd = computed(() => {
    if (!breadcrumbItems.value.length) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.value.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };
  });

  useHead(() => {
    if (!breadcrumbJsonLd.value) return {};

    return {
      script: [
        {
          key: 'breadcrumb-jsonld',
          type: 'application/ld+json',
          innerHTML: JSON.stringify(breadcrumbJsonLd.value),
        },
      ],
    };
  });
</script>

<style lang="scss" scoped></style>
